/**
 * Connect Platform Modal Component
 * Modal for connecting media platform accounts via OAuth or API credentials
 */

import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { clsx } from "clsx";
import {
  Loader2,
  ExternalLink,
  CheckCircle,
  XCircle,
  Info,
} from "lucide-react";
import { Modal } from "@/components";
import type { PlatformConfig, ConnectionMethod } from "@/types";
import { oauthInstructions } from "./oauthInstructions";
import {
  exchangeFacebookToken,
  exchangeGoogleToken,
  exchangeTikTokToken,
  getTokenExpiryDate,
} from "@/services/oauth/tokenExchange";
import { generateOAuthState } from "@/services/oauth/handlers";
import { MANUAL_METHODS, DEFAULT_MANUAL } from "./manualMethods";
import { validateGoogleServiceAccount } from "@/services/mediaApi/googleServiceAccountValidation";

export interface ConnectPlatformModalProps {
  isOpen: boolean;
  onClose: () => void;
  platform: PlatformConfig | null;
  onConnect: (credentials: {
    access_token: string;
    refresh_token?: string;
    account_id: string;
    account_name?: string;
    expires_at?: string;
    method?: ConnectionMethod;
    // Service-account only: routed to the service_role-only secrets table.
    service_account?: {
      service_account_json: string;
      impersonated_user: string;
      developer_token: string;
      login_customer_id?: string;
    };
  }) => Promise<void>;
  isConnecting?: boolean;
  workspaceId?: string;
  /** ชื่อ workspace ที่ pin ไว้ตอนเปิด — โมดัลย้ำให้คนเห็นก่อนตัดสินใจ */
  workspaceName?: string;
}


export const ConnectPlatformModal: React.FC<ConnectPlatformModalProps> = ({
  isOpen,
  onClose,
  platform,
  onConnect,
  isConnecting = false,
  workspaceId,
  workspaceName,
}) => {
  const { t } = useTranslation();
  const [apiKey, setApiKey] = useState("");
  const [secretKey, setSecretKey] = useState("");
  const [accountId, setAccountId] = useState("");
  const [accountName, setAccountName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validating, setValidating] = useState(false);
  // Service-account method (Google Ads) fields.
  const [saJson, setSaJson] = useState("");
  const [saUser, setSaUser] = useState("");
  const [saDevToken, setSaDevToken] = useState("");
  const [saLoginCustomerId, setSaLoginCustomerId] = useState("");

  const methods: ConnectionMethod[] = platform
    ? platform.connectionMethods ?? [platform.authType === "api_key" ? "token" : "oauth2"]
    : [];
  const [method, setMethod] = useState<ConnectionMethod>(methods[0] ?? "token");
  const [methodPlatform, setMethodPlatform] = useState(platform?.type);
  if (platform && platform.type !== methodPlatform) {
    setMethodPlatform(platform.type);
    setMethod(methods[0]);
  }

  const instructions = platform
    ? oauthInstructions[platform.type]
    : undefined;

  const cfg = platform ? (MANUAL_METHODS[platform.type] ?? DEFAULT_MANUAL) : DEFAULT_MANUAL;

  const resetForm = useCallback(() => {
    setApiKey("");
    setSecretKey("");
    setAccountId("");
    setAccountName("");
    setSaJson("");
    setSaUser("");
    setSaDevToken("");
    setSaLoginCustomerId("");
    setError(null);
    setSuccess(false);
    setValidating(false);
    setMethodPlatform(undefined);
  }, []);

  const handleClose = useCallback(() => {
    if (!isConnecting) {
      resetForm();
      onClose();
    }
  }, [isConnecting, onClose, resetForm]);

  const handleOAuthConnect = useCallback(async () => {
    if (!platform || !workspaceId) return;

    setError(null);

    // Use the shared generator rather than rolling one here. This built the state
    // from platform + workspaceId + Date.now() with no random component, so the
    // value the CSRF check compares against was derived entirely from things an
    // attacker knows or can narrow down. generateOAuthState has carried a
    // crypto.randomUUID nonce all along; this call site simply never used it.
    // generateOAuthState returns base64, whose padding "=" is not safe unencoded
    // in a query value — and whether padding appears at all depends only on the
    // length of window.location.origin, so this is invisible on one deployment
    // and present on every request from another. Measured across origins: "+"
    // and "/" never occur for this payload, "=" occurs on 0% or 100% of states
    // depending on the origin. Encoded below at each use, alongside redirect_uri
    // and scope, which were already encoded.
    const state = generateOAuthState(platform.type, workspaceId);

    // Construct OAuth URL based on platform
    let oauthUrl = "";
    const redirectUri = `${window.location.origin}/settings/integrations/callback`;

    switch (platform.type) {
      case "facebook": {
        const fbAppId = import.meta.env.VITE_FACEBOOK_APP_ID || "";
        if (!fbAppId) { setError(t("settings.oauthNotConfigured", { platform: "Facebook", envVar: "VITE_FACEBOOK_APP_ID" })); return; }
        oauthUrl = `https://www.facebook.com/v18.0/dialog/oauth?client_id=${fbAppId}&redirect_uri=${encodeURIComponent(
          redirectUri
        )}&scope=${encodeURIComponent(
          platform.scopes?.join(",") || "ads_read,ads_management"
        )}&response_type=code&state=${encodeURIComponent(state)}`;
        break;
      }

      case "google_ads":
      case "google_analytics": {
        const googleClientId =
          import.meta.env.VITE_GOOGLE_CLIENT_ID || "";
        if (!googleClientId) { setError(t("settings.oauthNotConfigured", { platform: "Google", envVar: "VITE_GOOGLE_CLIENT_ID" })); return; }
        // access_type=offline + prompt=consent so Google returns a refresh_token
        // (the worker needs it to keep syncing without re-auth).
        oauthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${googleClientId}&redirect_uri=${encodeURIComponent(
          redirectUri
        )}&scope=${encodeURIComponent(
          platform.scopes?.join(" ") || ""
        )}&response_type=code&access_type=offline&prompt=consent&state=${encodeURIComponent(state)}`;
        break;
      }

      case "tiktok": {
        const tiktokAppId = import.meta.env.VITE_TIKTOK_APP_ID || "";
        if (!tiktokAppId) { setError(t("settings.oauthNotConfigured", { platform: "TikTok", envVar: "VITE_TIKTOK_APP_ID" })); return; }
        // TikTok Marketing API uses the Business portal auth endpoint and only accepts
        // app_id/redirect_uri/state — scopes are configured on the app, not passed in the
        // URL — and it returns the code as `auth_code` (handled in OAuthCallback).
        oauthUrl = `https://business-api.tiktok.com/portal/auth?app_id=${tiktokAppId}&redirect_uri=${encodeURIComponent(
          redirectUri
        )}&state=${encodeURIComponent(state)}`;
        break;
      }

      default:
        setError("OAuth not configured for this platform");
        return;
    }

    // Open OAuth popup
    const width = 600;
    const height = 700;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    const popup = window.open(
      oauthUrl,
      "oauth-popup",
      `width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      setError(t("settings.oauthPopupBlocked"));
      return;
    }

    // Listen for OAuth callback
    const messageHandler = async (event: MessageEvent) => {
      // Verify origin
      if (event.origin !== window.location.origin) return;

      const { code, state: returnedState, error: oauthError } = event.data;

      if (oauthError) {
        setError(oauthError);
        popup.close();
        window.removeEventListener("message", messageHandler);
        return;
      }

      if (code && returnedState === state) {
        popup.close();
        window.removeEventListener("message", messageHandler);

        // Exchange code for token
        try {
          const redirectUri = `${window.location.origin}/settings/integrations/callback`;
          let accessTokenData: {
            access_token: string;
            refresh_token?: string;
            expires_in?: number;
          };

          // Exchange code for access token based on platform
          switch (platform.type) {
            case "facebook": {
              const fbToken = await exchangeFacebookToken(code, redirectUri);
              accessTokenData = {
                access_token: fbToken.access_token,
                expires_in: fbToken.expires_in,
              };
              break;
            }

            case "google_ads":
            case "google_analytics": {
              const googleToken = await exchangeGoogleToken(code, redirectUri);
              accessTokenData = {
                access_token: googleToken.access_token,
                refresh_token: googleToken.refresh_token,
                expires_in: googleToken.expires_in,
              };
              break;
            }

            case "tiktok": {
              const tiktokToken = await exchangeTikTokToken(code, redirectUri);
              accessTokenData = {
                access_token: tiktokToken.access_token,
                refresh_token: tiktokToken.refresh_token,
                expires_in: tiktokToken.expires_in,
              };
              break;
            }

            default:
              setError("OAuth not configured for this platform");
              return;
          }

          // Calculate expiry date
          const expiresAt = accessTokenData.expires_in
            ? getTokenExpiryDate(accessTokenData.expires_in)
            : undefined;

          // Connect with the actual access token
          await onConnect({
            access_token: accessTokenData.access_token,
            refresh_token: accessTokenData.refresh_token,
            account_id: accountId || "pending",
            account_name: accountName || undefined,
            expires_at: expiresAt,
            method: "oauth2",
          });
          setSuccess(true);
          setTimeout(() => {
            handleClose();
          }, 1500);
        } catch (err) {
          setError(
            err instanceof Error ? err.message : t('settings.connectFailed')
          );
        }
      }
    };

    window.addEventListener("message", messageHandler);
  }, [platform, workspaceId, accountId, accountName, onConnect, handleClose, t]);

  const handleTokenConnect = useCallback(async () => {
    if (!platform) return;
    if (validating) return;
    const c = MANUAL_METHODS[platform.type] ?? DEFAULT_MANUAL;
    // OAuth tokens / API keys never contain whitespace. Strip any that sneaks in
    // via copy-paste — a stray space mid-token (e.g. a line-wrapped paste) is
    // stored verbatim and later surfaces as an opaque `invalid_grant` at sync time.
    const token = apiKey.replace(/\s+/g, "");
    const secret = secretKey.replace(/\s+/g, "");
    if (!token) { setError(t('settings.pleaseEnter', { field: t(c.tokenLabel) })); return; }
    if (c.secretLabel && !secret) { setError(t('settings.pleaseEnter', { field: t(c.secretLabel) })); return; }
    if (!accountId) { setError(t('settings.pleaseEnter', { field: t(c.accountLabel) })); return; }

    const normalizedId = c.normalizeAccountId(accountId);
    setError(null);
    try {
      let resolvedName = accountName || undefined;
      if (c.validate) {
        setValidating(true);
        // Pass the 2nd secret only for platforms that have one, so single-secret
        // validators are still called with exactly (accountId, token).
        const v = c.secretLabel
          ? await c.validate(normalizedId, token, secret, t)
          : await c.validate(normalizedId, token, undefined, t);
        setValidating(false);
        if (!v.valid) { setError(v.error ?? t("settings.validationFailed")); return; }
        resolvedName = accountName || v.accountName;
      }
      const creds = { account_id: normalizedId, account_name: resolvedName, method: "token" as const };
      const withToken =
        c.storeTokenAs === "refresh_token"
          ? { ...creds, access_token: "", refresh_token: token }
          : { ...creds, access_token: token };
      // A 2nd secret (e.g. LINE's secret key) rides in refresh_token alongside
      // the access key in access_token.
      const finalCreds =
        c.secretLabel && c.storeSecretAs === "refresh_token"
          ? { ...withToken, refresh_token: secret }
          : withToken;
      await onConnect(finalCreds);
      setSuccess(true);
      setTimeout(() => { handleClose(); }, 1500);
    } catch (err) {
      setValidating(false);
      setError(err instanceof Error ? err.message : t('settings.connectFailed'));
    }
  }, [platform, apiKey, secretKey, accountId, accountName, onConnect, handleClose, validating, t]);

  const handleServiceAccountConnect = useCallback(async () => {
    if (!platform) return;
    if (validating) return;
    if (!saJson.trim()) { setError(t("settings.pleasePasteSaJson")); return; }
    if (!saUser.trim()) { setError("Please enter the impersonated user email"); return; }
    if (!saDevToken.trim()) { setError(t("settings.pleaseEnterDevToken")); return; }
    if (!accountId.trim()) { setError(t("settings.pleaseEnter", { field: t("settings.lblCustomerId") })); return; }

    // Google Ads customer/login-customer ids are stored digit-only (GoogleAdsClient
    // strips hyphens anyway); the account row's external_account_id must match.
    const customerId = accountId.replace(/[^0-9]/g, "");
    const loginCustomerId = saLoginCustomerId.replace(/[^0-9]/g, "") || undefined;
    setError(null);
    setValidating(true);
    try {
      const v = await validateGoogleServiceAccount({
        serviceAccountJson: saJson,
        impersonatedUser: saUser.trim(),
        developerToken: saDevToken.trim(),
        customerId,
        loginCustomerId,
      }, t);
      setValidating(false);
      if (!v.valid) { setError(v.error ?? t("settings.validationFailed")); return; }

      await onConnect({
        access_token: "", // SA auth mints a fresh token server-side per sync job
        account_id: customerId,
        account_name: accountName || v.accountName,
        method: "service_account",
        service_account: {
          service_account_json: saJson,
          impersonated_user: saUser.trim(),
          developer_token: saDevToken.trim(),
          login_customer_id: loginCustomerId,
        },
      });
      setSuccess(true);
      setTimeout(() => { handleClose(); }, 1500);
    } catch (err) {
      setValidating(false);
      setError(err instanceof Error ? err.message : t('settings.connectFailed'));
    }
  }, [platform, saJson, saUser, saDevToken, saLoginCustomerId, accountId, accountName, onConnect, handleClose, validating, t]);

  if (!platform) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('settings.connectPlatform', { platform: platform.displayName })}
      size="lg"
      description={
        platform.authType === "oauth2"
          ? t('settings.followSteps')
          : t('settings.enterCredentials')
      }
    >
      <div className="space-y-6">
        {workspaceName && (
          <div className="mb-4 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm text-neutral-700">
            {t('settings.willConnectTo')}{" "}
            <span className="font-medium text-ink-900">{workspaceName}</span>.
          </div>
        )}

        {/* Method toggle (only when a platform offers more than one) */}
        {methods.length > 1 && !success && (
          <div role="tablist" className="flex gap-2 border-b border-neutral-200">
            {methods.map((m) => (
              <button
                key={m}
                role="tab"
                aria-selected={method === m}
                onClick={() => { setMethod(m); setError(null); }}
                className={clsx(
                  "px-4 py-2 text-sm font-medium border-b-2 -mb-px",
                  method === m
                    ? "border-primary-600 text-ink-900"
                    : "border-transparent text-gray-600 hover:text-gray-700",
                )}
              >
                {t(m === "token" ? 'settings.methodToken' : m === "service_account" ? 'settings.methodServiceAccount' : 'settings.methodOauth')}
              </button>
            ))}
          </div>
        )}

        {/* Token form */}
        {method === "token" && !success && (
          <div className="space-y-4">
            {cfg.note && (
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-sm text-neutral-700">
                {t(cfg.note)}
              </div>
            )}

            {(cfg.steps ?? instructions?.steps ?? []).length > 0 && (
              <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-ink-900 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm text-primary-900">
                    <p className="font-medium mb-1">{t('settings.howToCredentials')}</p>
                    <ol className="space-y-1 ml-4 list-decimal">
                      {(cfg.steps ?? instructions?.steps ?? []).map((step, index) => (
                        <li key={index}>{t(step)}</li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="manual-token" className="block text-sm font-medium text-gray-700 mb-1">
                {t(cfg.tokenLabel)}
              </label>
              <input
                id="manual-token"
                type="password"
                value={apiKey}
                onChange={(e) => { setApiKey(e.target.value); setError(null); }}
                placeholder={t(cfg.tokenPlaceholder)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
            </div>

            {cfg.secretLabel && (
              <div>
                <label htmlFor="manual-secret" className="block text-sm font-medium text-gray-700 mb-1">
                  {t(cfg.secretLabel)}
                </label>
                <input
                  id="manual-secret"
                  type="password"
                  value={secretKey}
                  onChange={(e) => { setSecretKey(e.target.value); setError(null); }}
                  placeholder={cfg.secretPlaceholder ? t(cfg.secretPlaceholder) : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
                />
              </div>
            )}

            <div>
              <label htmlFor="manual-account-id" className="block text-sm font-medium text-gray-700 mb-1">
                {t(cfg.accountLabel)}
              </label>
              <input
                id="manual-account-id"
                type="text"
                value={accountId}
                onChange={(e) => { setAccountId(e.target.value); setError(null); }}
                placeholder={t(cfg.accountPlaceholder)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
            </div>

            <div>
              <label htmlFor="manual-account-name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.accountNameLabel')} <span className="text-gray-600">{t('settings.optional')}</span>
              </label>
              <input
                id="manual-account-name"
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={t('settings.accountNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* Service-account form (Google Ads, domain-wide delegation) */}
        {method === "service_account" && !success && (
          <div className="space-y-4">
            <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-ink-900 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm text-primary-900">
                  <p className="font-medium mb-1">{t('settings.saTitle')}</p>
                  <ol className="space-y-1 ml-4 list-decimal">
                    <li>{t('settings.saStep1')}</li>
                    <li>{t('settings.saStep2')} <code className="text-xs">https://www.googleapis.com/auth/adwords</code></li>
                    <li>{t('settings.saStep3')}</li>
                  </ol>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="sa-json" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.saJsonLabel')}
              </label>
              <textarea
                id="sa-json"
                value={saJson}
                onChange={(e) => { setSaJson(e.target.value); setError(null); }}
                placeholder='{"type":"service_account","project_id":"...","private_key":"-----BEGIN PRIVATE KEY-----\n..."}'
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-xs"
              />
              <p className="mt-1 text-xs text-gray-600">{t('settings.saJsonNote')}</p>
            </div>

            <div>
              <label htmlFor="sa-user" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.saUserLabel')}
              </label>
              <input
                id="sa-user"
                type="email"
                value={saUser}
                onChange={(e) => { setSaUser(e.target.value); setError(null); }}
                placeholder="ads-manager@your-domain.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
            </div>

            <div>
              <label htmlFor="sa-dev-token" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.saDevTokenLabel')}
              </label>
              <input
                id="sa-dev-token"
                type="password"
                value={saDevToken}
                onChange={(e) => { setSaDevToken(e.target.value); setError(null); }}
                placeholder={t('settings.saDevTokenPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
            </div>

            <div>
              <label htmlFor="sa-customer-id" className="block text-sm font-medium text-gray-700 mb-1">
                {t("settings.lblCustomerId")}
              </label>
              <input
                id="sa-customer-id"
                type="text"
                value={accountId}
                onChange={(e) => { setAccountId(e.target.value); setError(null); }}
                placeholder="123-456-7890"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
            </div>

            <div>
              <label htmlFor="sa-login-customer-id" className="block text-sm font-medium text-gray-700 mb-1">
                {t("settings.lblLoginCustomerId")} <span className="text-gray-600">{t("settings.optional")}</span>
              </label>
              <input
                id="sa-login-customer-id"
                type="text"
                value={saLoginCustomerId}
                onChange={(e) => { setSaLoginCustomerId(e.target.value); setError(null); }}
                placeholder={t('settings.saLoginCidPlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
            </div>

            <div>
              <label htmlFor="sa-account-name" className="block text-sm font-medium text-gray-700 mb-1">
                {t("settings.accountNameLabel")} <span className="text-gray-600">{t("settings.optional")}</span>
              </label>
              <input
                id="sa-account-name"
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={t('settings.accountNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent"
              />
            </div>
          </div>
        )}

        {/* OAuth method — server-side token exchange via /api/oauth/exchange.
            Dormant until the platform's OAuth env pair is set (see .env.example);
            handleOAuthConnect surfaces a clear error if the client id is missing. */}
        {method === "oauth2" && !success && (
          <div className="space-y-4">
            {instructions && (
              <div className="bg-primary-50 border border-primary-100 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-ink-900 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-primary-900 mb-2">{t('settings.howItWorks')}</h4>
                    <ol className="space-y-2 text-sm text-primary-900">
                      {instructions.steps.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <span className="font-medium mr-2">{index + 1}.</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="oauth-account-name" className="block text-sm font-medium text-gray-700 mb-1">
                {t("settings.accountNameLabel")} <span className="text-gray-600">{t("settings.optional")}</span>
              </label>
              <input
                id="oauth-account-name"
                type="text"
                value={accountName}
                onChange={(e) => setAccountName(e.target.value)}
                placeholder={t('settings.accountNamePlaceholder')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="oauth-account-id" className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.lblAccountIdPlain')} <span className="text-gray-600">{t('settings.optional')}</span>
              </label>
              <input
                id="oauth-account-id"
                type="text"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                placeholder="act_123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-transparent font-mono text-sm"
              />
              <p className="mt-1 text-xs text-gray-600">
                {t("settings.leaveBlankDefault", { platform: platform.displayName })}
              </p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center space-x-2 p-3 bg-error-50 border border-error-200 rounded-lg text-error-700 text-sm">
            <XCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="flex items-center justify-center space-x-2 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
            <CheckCircle size={20} className="animate-scale-in" />
            <span className="font-medium">{t('settings.connectSuccess')}</span>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      {!success && (
        <div className="mt-6 flex items-center justify-between">
          {instructions?.helpUrl && (
            <a
              href={instructions.helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ExternalLink size={14} className="mr-1" />
              {t('settings.viewDocumentation')}
            </a>
          )}

          <div className="flex items-center space-x-3 ml-auto">
            <button
              onClick={handleClose}
              disabled={isConnecting}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("common.cancel")}
            </button>

            {method === "service_account" ? (
              <button
                onClick={handleServiceAccountConnect}
                disabled={isConnecting || validating || !saJson || !saUser || !saDevToken || !accountId}
                className={clsx(
                  "inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg",
                  "bg-primary-600 text-ink-950 hover:bg-primary-700",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ink-950",
                  "transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {validating ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t("settings.validating")}
                  </>
                ) : isConnecting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t('settings.connecting')}
                  </>
                ) : (
                  t('settings.connect')
                )}
              </button>
            ) : method === "token" ? (
              <button
                onClick={handleTokenConnect}
                disabled={isConnecting || validating || !apiKey || !accountId || (!!cfg.secretLabel && !secretKey)}
                className={clsx(
                  "inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg",
                  "bg-primary-600 text-ink-950 hover:bg-primary-700",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ink-950",
                  "transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {validating ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t("settings.validating")}
                  </>
                ) : isConnecting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t('settings.connecting')}
                  </>
                ) : (
                  t('settings.connect')
                )}
              </button>
            ) : (
              <button
                onClick={handleOAuthConnect}
                disabled={isConnecting}
                className={clsx(
                  "inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg",
                  "bg-primary-600 text-ink-950 hover:bg-primary-700",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ink-950",
                  "transition-colors",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {isConnecting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    {t('settings.connecting')}
                  </>
                ) : (
                  t('settings.connect')
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ConnectPlatformModal;
